import {
  AnimatedLayout,
  AnimationProvider,
  Overlay,
  useAnimationLayout,
} from '@/components';
import Loading from '@/components/molecules/Loading/Loading';
import APP_CONFIG from '@/config/app';
import { AnimatePresence, motion } from 'framer-motion';
import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTrayNavigation } from '../../providers/common/TrayNavigationProvider';
import { TrayBottomDrawerProps, TrayProps } from './interfaces/Tray';
import TrayBody from './TrayBody';
import TrayBottomDrawer from './TrayBottomDrawer';
import TrayFooter from './TrayFooter';
import TrayHeader from './TrayHeader';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const {
  MAIN_PADDING: mainPadding,
  MAIN_GAP: mainGap,
  MAIN_ROUNDEDNESS: mainRoundedness,
  MAIN_BACKDROP_BLUR: mainBackdropBlur,
} = APP_CONFIG.STYLES;
const spacingConfig = {
  padding: `p${mainPadding}`,
  gap: `gap${mainGap}`,
};
const sizingConfig = {
  height: 'h-full',
  bottomDrawerSize: 'bottom-md left-md right-md',
};
const colorConfig = {
  background: `bg-primary/5 backdrop-blur backdrop-blur${mainBackdropBlur}`,
  border: 'border border-primary/5',
};
const roundednessConfig = {
  roundedness: `rounded${mainRoundedness}`,
};
const remToPx = (rem: number) => rem * 16;
const Tray: FC<
  TrayProps & {
    loading?: boolean;
    mode?: 'sideDrawer' | 'bottomDrawer';
    bottomDrawer?: TrayBottomDrawerProps;
    width?: number;
    distanceFromEdgesInRem?: number;
  }
> = ({
  isOpen,
  showHeaderCloseButton,
  onClose,
  slideFrom = 'right',
  className = '',
  screens,
  loading = false,
  mode = 'sideDrawer',
  bottomDrawer: bottomDrawerProps,
  width = 0.4,
  distanceFromEdgesInRem = 1.35,
}) => {
  const { setBack, setDirection } = useAnimationLayout();
  const {
    currentScreenIndex,
    navigateBack,
    bodySlideFrom,
    resetBodySlideFrom,
    setScreenIndex,
    goToScreen,
  } = useTrayNavigation();
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [containerStyle, setContainerStyle] = useState<CSSProperties>({
    position: 'fixed',
    zIndex: 20,
  });
  const trayRef = useRef<HTMLDivElement>(null);
  const marginEdges = useMemo(
    () => remToPx(Math.max(0, distanceFromEdgesInRem)),
    [distanceFromEdgesInRem],
  );
  const updateContainerStyle = useCallback(() => {
    const viewportTop = document.body.getBoundingClientRect().top;
    const newStyle: CSSProperties = {
      position: 'fixed',
      zIndex: 20,
      top: `${-viewportTop + marginEdges}px`,
      bottom: `${marginEdges}px`,
      height: `calc(${window.innerHeight}px - ${2 * marginEdges}px)`,
      left: 0,
      right: 0,
    };
    if (mode === 'sideDrawer') {
      const validWidth = Math.min(Math.max(width, 0.3), 0.8);
      newStyle.width = `${validWidth * 100}%`;
      newStyle.minWidth = '300px';
      newStyle.maxWidth = '80%';
      newStyle.right = `${marginEdges}px`;
      newStyle.left = 'auto';
    } else if (mode === 'bottomDrawer') {
      newStyle.bottom = `${marginEdges}px`;
      newStyle.left = `${marginEdges}px`;
      newStyle.right = `${marginEdges}px`;
      newStyle.height = `calc(${window.innerHeight}px * 0.7)`;
      newStyle.top = `calc(${window.innerHeight}px - ${newStyle.height} - ${marginEdges}px)`;
    }
    setContainerStyle(newStyle);
  }, [mode, width, marginEdges]);
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
      setDirection('open');
      setBack(false);
      setShouldRender(true);
      updateContainerStyle();
      window.addEventListener('resize', updateContainerStyle);
      return () => {
        window.removeEventListener('resize', updateContainerStyle);
      };
    } else {
      document.body.classList.remove('overflow-hidden');
      setDirection('close');
      setBack(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, setDirection, setBack, updateContainerStyle]);
  useEffect(() => {
    if (!isOpen) {
      setScreenIndex(0);
      resetBodySlideFrom();
    }
  }, [isOpen, resetBodySlideFrom, setScreenIndex]);
  useEffect(() => {
    const { navigateTo } = screens[currentScreenIndex];
    if (navigateTo !== undefined) {
      goToScreen(navigateTo, true);
    }
  }, [currentScreenIndex, goToScreen, screens]);
  if (!shouldRender) return null;
  const {
    headlineTitle,
    headlineCaption,
    footerContent,
    includeFooterCloseButton,
    includeFooterContinueButton,
    closeButtonLabel,
    continueButtonLabel,
    onNavigateForward,
    disableNavigatingBack,
    extraButton1Label,
    extraButton1OnClick,
    extraButton2Label,
    extraButton2OnClick,
    extraButton1Icon,
    extraButton2Icon,
    extraButton1Props,
    extraButton2Props,
    headerNode,
    rightButtons,
    onNavigateBack,
  } = screens[currentScreenIndex];
  const shouldRenderFooter =
    includeFooterCloseButton !== false ||
    includeFooterContinueButton !== false ||
    footerContent ||
    extraButton1Label ||
    extraButton2Label;
  const slideDirection =
    mode === 'bottomDrawer'
      ? 'BOTTOM'
      : (slideFrom.toUpperCase() as 'LEFT' | 'RIGHT');
  const slideAnimationConfig =
    APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM[
      slideDirection as keyof typeof APP_CONFIG.ANIMATION_SETTINGS.SLIDE_FROM
    ];
  const variants = {
    initial: slideAnimationConfig.initial,
    open: slideAnimationConfig.open,
    closed: slideAnimationConfig.closed,
  };
  return createPortal(
    <AnimationProvider>
      <AnimatePresence>
        {isOpen && (
          <>
            <AnimatePresence>
              <Overlay className="z-20" />
            </AnimatePresence>
            <motion.div
              ref={trayRef}
              initial="initial"
              animate="open"
              exit="closed"
              variants={variants}
              transition={APP_CONFIG.ANIMATION_SETTINGS.COMMON}
              style={containerStyle}
              className={`flex flex-col overflow-hidden h-full
                ${spacingConfig.padding} ${spacingConfig.gap} 
                ${colorConfig.background} ${colorConfig.border} 
                ${roundednessConfig.roundedness} ${className} 
                ${borderClassToVisualizeElementsAndSpacing}`}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <motion.div
                initial="initial"
                animate="open"
                exit="closed"
                variants={variants}
                transition={APP_CONFIG.ANIMATION_SETTINGS.COMMON}
                className={borderClassToVisualizeElementsAndSpacing}
              >
                <TrayHeader
                  title={screens[currentScreenIndex].title}
                  showCloseButton={showHeaderCloseButton}
                  onClose={onClose}
                  onNavigateBack={
                    currentScreenIndex > 0 && !disableNavigatingBack
                      ? onNavigateBack || navigateBack
                      : undefined
                  }
                  headerNode={headerNode}
                  rightButtons={rightButtons}
                />
              </motion.div>
              <AnimatedLayout
                uniqueKey={currentScreenIndex}
                slideFrom={bodySlideFrom}
                className={`relative w-full flex-1 overflow-hidden 
                  ${spacingConfig.gap}
                  ${borderClassToVisualizeElementsAndSpacing}`}
              >
                <TrayBody
                  headlineTitle={headlineTitle}
                  headlineCaption={headlineCaption}
                >
                  {screens[currentScreenIndex].content}
                </TrayBody>
              </AnimatedLayout>
              {shouldRenderFooter && (
                <motion.div
                  initial="initial"
                  animate="open"
                  exit="closed"
                  variants={variants}
                  transition={APP_CONFIG.ANIMATION_SETTINGS.COMMON}
                  className={borderClassToVisualizeElementsAndSpacing}
                >
                  <TrayFooter
                    includeCloseButton={includeFooterCloseButton ?? true}
                    closeButtonLabel={closeButtonLabel}
                    onClose={onClose}
                    includeContinueButton={includeFooterContinueButton ?? false}
                    continueButtonLabel={continueButtonLabel}
                    onContinue={onNavigateForward}
                    footerContent={footerContent}
                    extraButton1Label={extraButton1Label}
                    extraButton1OnClick={extraButton1OnClick}
                    extraButton2Label={extraButton2Label}
                    extraButton2OnClick={extraButton2OnClick}
                    extraButton1Icon={extraButton1Icon}
                    extraButton2Icon={extraButton2Icon}
                    extraButton1Props={extraButton1Props}
                    extraButton2Props={extraButton2Props}
                  />
                </motion.div>
              )}
              {loading && <LoadingOverlay />}
              {bottomDrawerProps && bottomDrawerProps.isOpen && (
                <BottomDrawerOverlay />
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
      {bottomDrawerProps && (
        <TrayBottomDrawer
          isOpen={bottomDrawerProps.isOpen}
          showHeaderCloseButton={bottomDrawerProps.showHeaderCloseButton}
          onClose={bottomDrawerProps.onClose}
          headerTitle={bottomDrawerProps.headerTitle}
          headerContent={bottomDrawerProps.headerContent}
          headerRightButtons={bottomDrawerProps.headerRightButtons}
          bodyHeadlineTitle={bottomDrawerProps.bodyHeadlineTitle}
          bodyHeadlineCaption={bottomDrawerProps.bodyHeadlineCaption}
          bodyChildren={bottomDrawerProps.bodyChildren}
          footerContent={bottomDrawerProps.footerContent}
          includeFooterCloseButton={bottomDrawerProps.includeFooterCloseButton}
          footerCloseButtonLabel={bottomDrawerProps.footerCloseButtonLabel}
          parentRef={trayRef}
        />
      )}
    </AnimationProvider>,
    document.body,
  );
};
const LoadingOverlay = () => (
  <Overlay>
    <Loading />
  </Overlay>
);
const BottomDrawerOverlay = () => <Overlay />;
export default Tray;
